import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  TextField, RadioGroup, Radio, FormControlLabel, Typography, Chip, Alert,
  useMediaQuery, useTheme,
} from '@mui/material';
import type { Vacacion, RegistrarGoceRequest } from '../types/beneficios';

interface Props {
  open: boolean;
  onClose: () => void;
  vacacion: Vacacion | null;
  onSave: (request: RegistrarGoceRequest) => Promise<void>;
}

function contarDiasInhabiles(inicio: string, fin: string): number {
  if (!inicio || !fin) return 0;
  let count = 0;
  const d = new Date(inicio);
  const end = new Date(fin);
  while (d <= end) {
    const dow = d.getDay();
    if (dow === 0 || dow === 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

export default function GoceVentaDialog({ open, onClose, vacacion, onSave }: Props) {
  const [tipo, setTipo] = useState<'GOCE' | 'VENTA'>('GOCE');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [diasEfectivos, setDiasEfectivos] = useState(0);
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto-calculated
  const [diasCalendario, setDiasCalendario] = useState(0);
  const [diasInhabiles, setDiasInhabiles] = useState(0);

  useEffect(() => {
    if (open) {
      setTipo('GOCE');
      setFechaInicio('');
      setFechaFin('');
      setDiasEfectivos(0);
      setObservaciones('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (fechaInicio && fechaFin && fechaFin >= fechaInicio) {
      const cal = Math.floor((new Date(fechaFin).getTime() - new Date(fechaInicio).getTime()) / 86400000) + 1;
      const inh = contarDiasInhabiles(fechaInicio, fechaFin);
      setDiasCalendario(cal);
      setDiasInhabiles(inh);
      setDiasEfectivos(cal);
    } else {
      setDiasCalendario(0);
      setDiasInhabiles(0);
      setDiasEfectivos(0);
    }
  }, [fechaInicio, fechaFin]);

  const diasVendidosActuales = vacacion?.goces?.filter(g => g.tipoDevengue === 'VENTA').reduce((s, g) => s + g.diasEfectivos, 0) ?? 0;
  const saldoDisponible = vacacion?.diasPendientes ?? 0;

  const handleSave = async () => {
    if (!vacacion) return;
    setError(null);

    if (!fechaInicio || !fechaFin) {
      setError('Debe ingresar ambas fechas');
      return;
    }
    if (fechaFin < fechaInicio) {
      setError('La fecha fin debe ser mayor o igual a la fecha inicio');
      return;
    }
    if (diasEfectivos <= 0) {
      setError('Los días efectivos deben ser mayor a 0');
      return;
    }
    if (diasEfectivos > saldoDisponible) {
      setError(`Saldo insuficiente. Disponible: ${saldoDisponible} días`);
      return;
    }
    if (tipo === 'VENTA' && diasVendidosActuales + diasEfectivos > 15) {
      setError(`Máximo 15 días de venta por periodo. Ya vendidos: ${diasVendidosActuales}`);
      return;
    }

    setSaving(true);
    try {
      await onSave({
        idVacacion: vacacion.idVacacion,
        tipoDevengue: tipo,
        fechaInicio,
        fechaFin,
        diasEfectivos,
        observaciones: observaciones || null,
      });
      onClose();
    } catch {
      setError('Error al registrar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Registrar Goce / Venta de Vacaciones</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {vacacion && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={`Saldo: ${saldoDisponible} días`} color="primary" variant="outlined" />
              {tipo === 'VENTA' && (
                <Chip label={`Vendidos: ${diasVendidosActuales}/15`} color="warning" variant="outlined" size="small" />
              )}
            </Stack>
          )}

          <RadioGroup row value={tipo} onChange={(e) => setTipo(e.target.value as 'GOCE' | 'VENTA')}>
            <FormControlLabel value="GOCE" control={<Radio />} label="Goce (tomar vacaciones)" />
            <FormControlLabel value="VENTA" control={<Radio />} label="Venta de días" />
          </RadioGroup>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Fecha Inicio" type="date" fullWidth
              value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" label="Fecha Fin" type="date" fullWidth
              value={fechaFin} onChange={(e) => setFechaFin(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <TextField size="small" label="Días Calendario" type="number" value={diasCalendario}
              slotProps={{ input: { readOnly: true } }} sx={{ width: { xs: 'calc(50% - 8px)', sm: 140 } }} />
            <TextField size="small" label="Días Inhábiles" type="number" value={diasInhabiles}
              slotProps={{ input: { readOnly: true } }} sx={{ width: { xs: 'calc(50% - 8px)', sm: 140 } }}
              helperText="Sáb/Dom" />
            <TextField size="small" label="Días Efectivos" type="number" value={diasEfectivos}
              onChange={(e) => setDiasEfectivos(Number(e.target.value))}
              sx={{ width: { xs: '100%', sm: 140 } }} />
          </Stack>

          {tipo === 'VENTA' && diasEfectivos > 0 && (
            <Typography variant="body2" color="text.secondary">
              El monto de venta se calculará automáticamente: RemComputable / 30 × {diasEfectivos} días
            </Typography>
          )}

          <TextField size="small" label="Observaciones" multiline rows={2} fullWidth
            value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Registrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
